import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { axiosInstance } from '@/lib/axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft,
  UserCheck,
  UserX,
  Clock,
  Mail,
  Briefcase,
  Users,
} from 'lucide-react';

const roleLabel = {
  worker: 'Worker',
  supervisor: 'Supervisor',
  company_admin: 'Company Admin',
};

const roleColor = {
  worker: 'bg-blue-100 text-blue-800',
  supervisor: 'bg-purple-100 text-purple-800',
  company_admin: 'bg-orange-100 text-orange-800',
};

export default function AdminPendingApprovals() {
  const navigate = useNavigate();
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/users/pending-approvals');
      setPending(data.pending || []);
    } catch (e) {
      toast.error('Failed to load pending approvals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const approve = async (u) => {
    setBusy(u.id);
    try {
      await axiosInstance.post(`/users/${u.id}/approve`);
      toast.success(`Approved ${u.name}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approve failed');
    } finally {
      setBusy(null);
    }
  };

  const reject = async (u) => {
    const reason = window.prompt(
      `Reject ${u.name}? Optional: enter a reason for the audit trail.`,
      ''
    );
    if (reason === null) return;
    setBusy(u.id);
    try {
      await axiosInstance.post(`/users/${u.id}/reject`, { reason });
      toast.success(`Rejected ${u.name}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reject failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => navigate('/admin')}
          data-testid="pending-back-btn"
          className="mb-4 flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Dashboard
        </button>

        <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Users className="w-8 h-8 text-amber-600" />
              Pending Worker Approvals
            </h1>
            <p className="text-gray-600 mt-1 max-w-2xl">
              Workers who signed up with your company code but haven't been reviewed yet.
              Approve to grant full access, or reject to block them. Rejected accounts
              are kept for audit but cannot log in.
            </p>
          </div>
          <Badge className="bg-amber-100 text-amber-900 text-base px-3 py-1">
            {pending.length} pending
          </Badge>
        </div>

        {loading ? (
          <div className="text-center py-16 text-gray-500">Loading...</div>
        ) : pending.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <UserCheck className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                All caught up
              </h3>
              <p className="text-gray-600 max-w-md mx-auto">
                No workers are waiting for approval right now. New self-registrations
                will appear here automatically.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {pending.map((u) => (
              <Card key={u.id} data-testid={`pending-card-${u.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between flex-wrap gap-2">
                    <div>
                      <CardTitle className="text-lg">{u.name}</CardTitle>
                      <div className="flex items-center gap-2 flex-wrap mt-1">
                        <Badge className={`${roleColor[u.role] || 'bg-gray-100 text-gray-800'} text-xs`}>
                          {roleLabel[u.role] || u.role}
                        </Badge>
                        {u.craft && (
                          <Badge variant="outline" className="text-xs">
                            <Briefcase className="w-3 h-3 mr-1" /> {u.craft}
                          </Badge>
                        )}
                        {u.divisionName && (
                          <Badge variant="outline" className="text-xs">
                            {u.divisionName}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {u.createdAt ? new Date(u.createdAt).toLocaleString() : ''}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-700 flex items-center gap-2 mb-3">
                    <Mail className="w-4 h-4 text-gray-400" />
                    {u.email}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => approve(u)}
                      disabled={busy === u.id}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid={`approve-btn-${u.id}`}
                    >
                      <UserCheck className="w-4 h-4 mr-1" /> Approve
                    </Button>
                    <Button
                      onClick={() => reject(u)}
                      disabled={busy === u.id}
                      variant="outline"
                      className="text-red-600 hover:bg-red-50 border-red-200"
                      data-testid={`reject-btn-${u.id}`}
                    >
                      <UserX className="w-4 h-4 mr-1" /> Reject
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
